import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { ConfirmAlert, SimpleAlert } from "../components/Alerts/Alerts";
import { startChangeSeats } from "../actions/featureAddons";
import { useTranslationApp } from "../lib/i18next";
import { useDispatchApp } from "../lib/redux";
import {
  ALERT_ICON_TYPE_ERROR,
  ALERT_ICON_TYPE_INFO,
  ALERT_ICON_TYPE_SUCCESS,
} from "../utils/constants";
import {
  BUTTON_GO_TO_BILLING,
  EXTRA_SEATS_ADDED,
  EXTRA_SEATS_CANCEL_NOTICE,
  EXTRA_SEATS_CHANGE_SAVED,
  EXTRA_SEATS_FORBIDDEN,
  EXTRA_SEATS_MISSING_AGENCY,
  EXTRA_SEATS_REACTIVATE_NOT_ELIGIBLE,
  EXTRA_SEATS_REDUCE_NOTICE,
  EXTRA_SEATS_RETRY,
  SEATS_NOT_ELIGIBLE,
  SEATS_OPERATION_IN_PROGRESS,
  SEATS_PRICE_NOT_CONFIGURED,
  SEATS_QUANTITY_CHANGED,
  SEATS_QUANTITY_OUT_OF_RANGE,
  SEATS_SUBSCRIPTION_NOT_FOUND,
  SEATS_UNPAID,
} from "../i18n/keysTranslations";
import {
  SEATS_CHANGE_OUTCOME,
  SEATS_NOT_ELIGIBLE_MESSAGE,
  SEATS_QUANTITY_FALLBACK_MAX,
  clearSeatDraftUser,
  interpretChangeSeatsResult,
  resolveSeatsNotEligibleMessage,
  saveSeatDraftUser,
  shouldReuseSeatsRequestID,
} from "../utils/extraSeats";

const createRequestID = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : uuidv4();

const renewalDate = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return new Date();
  return new Date(numeric);
};

const NOT_ELIGIBLE_TITLE = {
  [SEATS_NOT_ELIGIBLE_MESSAGE.REACTIVATE]: EXTRA_SEATS_REACTIVATE_NOT_ELIGIBLE,
  [SEATS_NOT_ELIGIBLE_MESSAGE.PRICE]: SEATS_PRICE_NOT_CONFIGURED,
  [SEATS_NOT_ELIGIBLE_MESSAGE.SUBSCRIPTION]: SEATS_SUBSCRIPTION_NOT_FOUND,
  [SEATS_NOT_ELIGIBLE_MESSAGE.GENERIC]: SEATS_NOT_ELIGIBLE,
};

const INFO_OUTCOMES = new Set([
  SEATS_CHANGE_OUTCOME.QUANTITY_CHANGED,
  SEATS_CHANGE_OUTCOME.IN_PROGRESS,
]);

/** Copy for a failed seats call. Empty message means there is nothing to show. */
export const seatsChangeErrorMessage = ({ result, t, intent = "" }) => {
  const interpreted = interpretChangeSeatsResult(result);
  const data = interpreted.data;
  const outcome = interpreted.outcome;

  if (outcome === SEATS_CHANGE_OUTCOME.QUANTITY_CHANGED) {
    return {
      interpreted,
      message: t(SEATS_QUANTITY_CHANGED),
      goToBilling: false,
      tone: "info",
    };
  }

  if (outcome === SEATS_CHANGE_OUTCOME.IN_PROGRESS) {
    return {
      interpreted,
      message: t(SEATS_OPERATION_IN_PROGRESS),
      goToBilling: false,
      tone: "info",
    };
  }

  if (outcome === SEATS_CHANGE_OUTCOME.UNPAID) {
    return {
      interpreted,
      message: t(SEATS_UNPAID),
      goToBilling: true,
      tone: "warning",
    };
  }

  if (outcome === SEATS_CHANGE_OUTCOME.FORBIDDEN) {
    return {
      interpreted,
      message: t(EXTRA_SEATS_FORBIDDEN),
      goToBilling: false,
      tone: "error",
    };
  }

  if (outcome === SEATS_CHANGE_OUTCOME.OUT_OF_RANGE) {
    return {
      interpreted,
      message: t(SEATS_QUANTITY_OUT_OF_RANGE, {
        min: data?.minQuantity ?? 0,
        max: data?.maxQuantity ?? SEATS_QUANTITY_FALLBACK_MAX,
      }),
      goToBilling: false,
      tone: "error",
    };
  }

  if (outcome === SEATS_CHANGE_OUTCOME.NOT_ELIGIBLE) {
    const message = resolveSeatsNotEligibleMessage({
      code: interpreted.code,
      reason: data?.reason,
      intent,
    });
    return {
      interpreted,
      message: t(NOT_ELIGIBLE_TITLE[message]),
      goToBilling: false,
      tone: "error",
    };
  }

  if (outcome === SEATS_CHANGE_OUTCOME.MISSING_PARAMETERS) {
    return {
      interpreted,
      message: t(EXTRA_SEATS_MISSING_AGENCY),
      goToBilling: false,
      tone: "error",
    };
  }

  if (outcome === SEATS_CHANGE_OUTCOME.ERROR) {
    return {
      interpreted,
      message: t(EXTRA_SEATS_RETRY),
      goToBilling: false,
      tone: "error",
    };
  }

  return { interpreted, message: "", goToBilling: false, tone: "" };
};

export const presentSeatsApiError = async ({
  result,
  t,
  navigate,
  intent = "",
}) => {
  const presentation = seatsChangeErrorMessage({ result, t, intent });
  if (!presentation.message) return presentation.interpreted;

  if (presentation.goToBilling) {
    const confirmed = await ConfirmAlert({
      title: presentation.message,
      icon: "warning",
      confirmButtonText: t(BUTTON_GO_TO_BILLING),
    });
    if (confirmed?.isConfirmed) navigate("/billing");
    return presentation.interpreted;
  }

  SimpleAlert({
    title: presentation.message,
    icon: INFO_OUTCOMES.has(presentation.interpreted.outcome)
      ? ALERT_ICON_TYPE_INFO
      : ALERT_ICON_TYPE_ERROR,
  });
  return presentation.interpreted;
};

const successCopy = ({ interpreted, t, purpose, targetQuantity }) => {
  if (interpreted.outcome === SEATS_CHANGE_OUTCOME.SCHEDULED) {
    const date = renewalDate(interpreted.data?.effectiveAt);
    if (Number(targetQuantity) === 0) {
      return t(EXTRA_SEATS_CANCEL_NOTICE, { date });
    }
    return t(EXTRA_SEATS_REDUCE_NOTICE, {
      n: targetQuantity,
      date,
    });
  }

  if (purpose === "add") return t(EXTRA_SEATS_ADDED);
  return t(EXTRA_SEATS_CHANGE_SAVED);
};

const useChangeSeats = ({
  returnTo,
  onCompleted = () => {},
  onQuantityChanged = () => {},
} = {}) => {
  const dispatch = useDispatchApp();
  const navigate = useNavigate();
  const { t } = useTranslationApp();
  const requestRef = useRef({ id: "", target: null });

  const requestIDFor = useCallback((targetQuantity) => {
    if (
      requestRef.current.target !== targetQuantity ||
      !requestRef.current.id
    ) {
      requestRef.current = {
        id: createRequestID(),
        target: targetQuantity,
      };
    }
    return requestRef.current.id;
  }, []);

  const submitSeatsChange = useCallback(
    async ({
      targetQuantity,
      expectedQuantity,
      draftUser = null,
      purpose = "manage",
    }) => {
      const result = await dispatch(
        startChangeSeats({
          targetQuantity,
          expectedQuantity,
          requestID: requestIDFor(targetQuantity),
          origin: window.location.origin,
          returnTo,
        })
      );
      const interpreted = interpretChangeSeatsResult(result);

      if (interpreted.outcome === SEATS_CHANGE_OUTCOME.CHECKOUT) {
        if (draftUser) saveSeatDraftUser({ draft: draftUser });
        else clearSeatDraftUser();
        requestRef.current = { id: "", target: null };
        window.location.assign(interpreted.data.paymentUrl);
        return interpreted;
      }

      if (
        interpreted.outcome === SEATS_CHANGE_OUTCOME.APPLIED ||
        interpreted.outcome === SEATS_CHANGE_OUTCOME.SCHEDULED
      ) {
        requestRef.current = { id: "", target: null };
        SimpleAlert({
          title: successCopy({ interpreted, t, purpose, targetQuantity }),
          icon:
            interpreted.outcome === SEATS_CHANGE_OUTCOME.SCHEDULED
              ? ALERT_ICON_TYPE_INFO
              : ALERT_ICON_TYPE_SUCCESS,
        });
        onCompleted(interpreted);
        return interpreted;
      }

      if (!shouldReuseSeatsRequestID(interpreted.outcome)) {
        requestRef.current = { id: "", target: null };
      }

      await presentSeatsApiError({ result, t, navigate });
      if (interpreted.outcome === SEATS_CHANGE_OUTCOME.QUANTITY_CHANGED) {
        onQuantityChanged(interpreted);
      }
      return interpreted;
    },
    [
      dispatch,
      navigate,
      onCompleted,
      onQuantityChanged,
      requestIDFor,
      returnTo,
      t,
    ]
  );

  return { submitSeatsChange };
};

export default useChangeSeats;
